import type { Progress } from '../api/types'
import type { Coverage } from '../lib/coverage'
import type { LocaleLabel } from '../lib/locales'

const barTone = (pct: number) =>
  pct >= 95 ? 'bg-emerald-500' : pct >= 60 ? 'bg-blue-500' : 'bg-amber-500'

export function ProgressRow({ label, value }: { label: LocaleLabel; value: Progress }) {
  const pct = value.pct
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="flex items-baseline gap-1.5 font-medium text-ink-800">
          <span className="font-mono text-2xs text-ink-600">{label.code}</span>
          {label.variant && (
            <span className="chip border-ink-300 bg-ink-100 text-ink-700">{label.variant}</span>
          )}
          <span className="text-ink-600">{label.full}</span>
        </span>
        <span className="tabular-nums text-ink-600">
          {value.translated}/{value.total}
          <span className="ml-1 font-semibold text-ink-800">{pct}%</span>
        </span>
      </div>
      <div
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink-200"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label.full}: ${pct}% translated`}
      >
        <div className={`h-full rounded-full ${barTone(pct)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

const num = (value: number) => value.toLocaleString('en-US')

/** Per-locale progress for a grid column header: percentage + a bar that
 *  separates own translations from wording inherited from the default project. */
export function ColumnProgress({ name, coverage }: { name: string; coverage: Coverage }) {
  const { own, fallback, missing, total, pct } = coverage
  const ownPct = total === 0 ? 0 : (own / total) * 100
  const fallbackPct = total === 0 ? 0 : (fallback / total) * 100
  const summary =
    `${name}: ${pct}% of ${num(total)} keys resolve` +
    (fallback > 0 ? ` — ${num(own)} own, ${num(fallback)} inherited` : ` — ${num(own)} translated`) +
    `, ${num(missing)} missing`

  return (
    <div className="mt-0.5 flex items-center gap-1.5" title={summary}>
      <div
        className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-ink-300"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={summary}
      >
        <div className="flex h-full">
          <div className={barTone(pct)} style={{ width: `${ownPct}%` }} />
          {fallbackPct > 0 && (
            <div className={`${barTone(pct)} opacity-40`} style={{ width: `${fallbackPct}%` }} />
          )}
        </div>
      </div>
      <span className="shrink-0 font-semibold tabular-nums text-ink-700">{pct}%</span>
    </div>
  )
}
